import * as z from 'zod'
import { NumeroONullZod, NumeroOpcionalZod, StringOpcionalZod, StringZod } from '../zodComun'
import { DispositivoZod } from '../dispositivo'
import { FechaZod, FechaAISOZod } from '../fechas'

// ============= TIPOS BASE ESPECÍFICOS =============

export const DatosAdicionalesZod = z.record(z.string(), z.unknown())
export type DatosAdicionales = z.infer<typeof DatosAdicionalesZod>

export const SolicitudIDZod = z.string().uuid()
export type SolicitudID = z.infer<typeof SolicitudIDZod>

// Transformación consistente para booleanos desde string
const BooleanFromStringZod = z.union([z.boolean(), z.string()])
  .transform(val => typeof val === 'boolean' ? val : val === 'si')

// ============= OBJETIVOS Y DESTINATARIOS =============

export const ObjetivoNotificacionZod = z.object({
  terminalID: StringOpcionalZod,
  sesionID: StringZod.optional(),
  dispositivo: StringZod.optional()
})
export type ObjetivoNotificacion = z.infer<typeof ObjetivoNotificacionZod>

// ============= CONFIGURACIÓN PARA NOTIFICACIONES EN APP DESDE PUSH =============

export const TipoNotificacionEnAppZod = z.enum(['exito', 'info', 'atencion', 'error'])
export type TipoNotificacionEnApp = z.infer<typeof TipoNotificacionEnAppZod>

export const DataNotificacionEnAppZod = z.object({
  // Tipo de notificación en app (determina color/icono)
  tipo: TipoNotificacionEnAppZod,
  
  // Código opcional para mostrar en la notificación
  codigo: z.string().optional(),
  
  // Configuración de icono de NotificacionEnApp
  icono: z.string().optional(),
  iconoColor: z.string().optional(),
  iconoMedida: z.string().optional(),

  textoUrlQueAbrir: z.string().optional(),
  iconoUrlQueAbrir: z.string().optional(),
  
  // Configuración de comportamiento
  centrado: z.boolean().optional(),
  cerrable: z.boolean().optional(),
  duracionSegundos: z.number().int().positive().optional(),

  // Timestamp de envío (ISO string para compatibilidad con Firebase/WebPush)
  fechaDeEnvio: FechaAISOZod,

  // Solicitar al cliente que actualice la app (abre Play Store / App Store)
  solicitarActualizarApp: z.literal(true).optional(),

  // Notificación silenciosa: no mostrar UI, solo disparar actualización de datos
  silenciosa: z.literal(true).optional(),

  // Identificador de terminal de destino para notificaciones silenciosas
  terminalIDDestino: z.string().optional(),

  // Contexto de acción al interactuar con la notificación (opcional).
  // Permite al cliente enrutar a la sesión y gimnasio correctos al tocar,
  // útil en terminales con múltiples sesiones activas o múltiples gimnasios.
  usuarioID: z.string().optional(),
  gimnasioID: z.string().optional(),
  instanciaID: z.string().optional(),

  // Botón de acción principal (ya soportado por modalRemoto)
  accion: z.string().optional(),
  payload: z.unknown().optional(),
  textoBotonAccion: z.string().optional(),

  // Botón de cancelación opcional para modalRemoto
  mostrarBotonDeCancelar: z.boolean().optional(),
  emitirAlCancelar: z.string().optional(),
  payloadAlCancelar: z.unknown().optional(),
  textoBotonCancelar: z.string().optional()
}).passthrough() // Permitir propiedades adicionales no especificadas
export type DataNotificacionEnApp = z.infer<typeof DataNotificacionEnAppZod>

// Alias para compatibilidad con código existente
export type TiposDeNotificacionEnApp = TipoNotificacionEnApp

// ============= NOTIFICACION EN APP (UI) =============

export type NotificacionEnApp = {
  titulo: string
  texto?: string

  id: string

  tipo: TiposDeNotificacionEnApp
  codigo?: string

  icono?: string
  iconoColor?: string
  iconoMedida?: string

  centrado?: boolean
  cerrable?: boolean
  cerrada?: boolean

  // En el frontend se usa Dayjs, en backend se puede usar Date
  creacion?: Date
  duracionSegundos: number
}

// Tipo auxiliar para crear notificaciones (sin id ni tipo requeridos)
export type NotificacionData = Required<Pick<NotificacionEnApp, 'titulo' | 'texto'>> & Partial<Omit<NotificacionEnApp, 'id' | 'tipo' | 'titulo' | 'texto'>>

export const NotificacionZod = z.object({
  id: z.string(),

  // Contenido
  titulo: z.string().min(1, 'El título es requerido'),
  texto: z.string().min(1, 'El texto es requerido'),
  
  // NATIVO
  icono: z.url().optional().catch(undefined),
  urlQueAbrir: z.url().optional().catch(undefined),
  imagen: z.url().optional().catch(undefined),
  insignia: z.string().optional(),

  // Datos adicionales tipados para notificaciones en app
  data: DataNotificacionEnAppZod,

  // Programación con transformación consistente
  fechaEnQueMostrar: z.coerce.date(),
  requerirInteraccion: BooleanFromStringZod.optional()
})
export type Notificacion = z.infer<typeof NotificacionZod>

export const NotificacionAumentadaZod = NotificacionZod.extend(ObjetivoNotificacionZod.shape)
export type NotificacionAumentada = z.infer<typeof NotificacionAumentadaZod>

export const ArrayDeNotificacionesAumentadasZod = z.array(NotificacionAumentadaZod)
export type ArrayDeNotificacionesAumentadas = z.infer<typeof ArrayDeNotificacionesAumentadasZod>


// ============= COMUNICACIÓN CON APPS =============

export const PaqueteDesdeAppZod = z.discriminatedUnion('accion', [
  z.object({ accion: z.literal('recibirMensajesPendientes') }),
  z.object({ accion: z.literal('recibirNotificacionesPendientes') }),
  z.object({ accion: z.literal('cancelarTodas') }),
  z.object({
    accion: z.literal('programarNotificacion'),
    notification: NotificacionAumentadaZod
  }),
  z.object({
    accion: z.literal('programarSoloEstasNotificaciones'),
    notificaciones: z.array(NotificacionAumentadaZod)
  })
])
export type PaqueteDesdeApp = z.infer<typeof PaqueteDesdeAppZod>

const PaqueteHaciaAppBaseZod = z.object({
  url: z.string().optional(),
  data: NotificacionAumentadaZod.or(z.record(z.string(), z.any()))
})

export const PaqueteHaciaAppZod = z.discriminatedUnion('accion', [
  PaqueteHaciaAppBaseZod.extend({ accion: z.literal('notiClick') }),
  PaqueteHaciaAppBaseZod.extend({ accion: z.literal('notiClose') }),
  PaqueteHaciaAppBaseZod.extend({ accion: z.literal('notificacionRecibida') }),
  PaqueteHaciaAppBaseZod.extend({ accion: z.literal('mensajeSSE') })
])
export type PaqueteHaciaApp = z.infer<typeof PaqueteHaciaAppZod>

// ============= SUSCRIPCIONES PUSH =============

export const WebPushSubscriptionZod = z.object({
  endpoint: StringZod,
  expirationTime: z.number().nullable().optional(),
  keys: z.object({
    p256dh: StringZod,
    auth: StringZod
  })
})
export type WebPushSubscription = z.infer<typeof WebPushSubscriptionZod>

export const SuscripcionBaseZod = z.object({
  appID: StringZod,
  dispositivo: DispositivoZod,
  origin: StringZod,
  creacion: FechaZod,
  expiracion: FechaZod,

  // Info extra de version
  version: StringOpcionalZod,
  versionCode: NumeroOpcionalZod
})

export const SuscripcionPushFirebaseZod = SuscripcionBaseZod.extend({
  tipo: z.literal('firebase'),
  tokenFirebase: StringZod
})

export const SuscripcionPushWebZod = SuscripcionBaseZod.extend({
  tipo: z.literal('web'),
  suscripcionWebPush: WebPushSubscriptionZod
})

export const SuscripcionPushZod = z.discriminatedUnion('tipo', [
  SuscripcionPushFirebaseZod,
  SuscripcionPushWebZod
])
export type SuscripcionPush = z.infer<typeof SuscripcionPushZod>


// ============= IDENTIFICADORES Y SESIONES =============

export const IdentificadorZod = z.object({
  terminalID: StringZod,
  usuarioID: StringOpcionalZod
})
export type Identificador = z.infer<typeof IdentificadorZod>

export const IdentificadorActivoZod = IdentificadorZod.extend({
  fechaDeSuscripcion: FechaZod,
  fechaDeExpiracion: FechaZod,

  appID: StringOpcionalZod,
  dispositivo: StringOpcionalZod,
  version: StringOpcionalZod,
  versionCode: NumeroOpcionalZod,
  gimnasioID: StringOpcionalZod
})
export type IdentificadorActivo = z.infer<typeof IdentificadorActivoZod>

// ============= HISTORIAL DE NOTIFICACIONES =============

export const NotificacionRecibidaZod = z.object({
  notificacion: NotificacionAumentadaZod,
  fecha: FechaZod
})
export type NotificacionRecibida = z.infer<typeof NotificacionRecibidaZod>

export const NotificacionEnviadaZod = z.object({
  solicitudID: SolicitudIDZod,
  notificacion: NotificacionAumentadaZod,
  fecha: FechaZod
})
export type NotificacionEnviada = z.infer<typeof NotificacionEnviadaZod>

// Campos comunes del historial
const { titulo, texto, icono, urlQueAbrir, imagen, fechaEnQueMostrar } = NotificacionAumentadaZod.shape
export const NotificacionParaHistorialZod = z.object({
  titulo, 
  texto, 
  icono, 
  urlQueAbrir, 
  imagen, 
  fechaEnQueMostrar,
  requerirInteraccion: z.boolean().optional(),
  creacion: FechaZod,
  exitoso: z.boolean()
})
export type NotificacionParaHistorial = z.infer<typeof NotificacionParaHistorialZod>


// ============= SUSCRIPCIONES UNIFICADAS =============

export const SuscripcionPushTerminalZod = z.object({
  creacion: FechaZod,
  expiracion: FechaZod,
  suscripcion: SuscripcionPushZod,
  historial: z.array(NotificacionParaHistorialZod).optional()
})
export type SuscripcionPushTerminal = z.infer<typeof SuscripcionPushTerminalZod>

// ============= EMISORES DE NOTIFICACIONES =============

export const EmisorDeNotificacionesZod = IdentificadorZod.extend({
  historialEnviadas: z.array(NotificacionEnviadaZod).optional(),
  creacion: FechaZod.optional()
})
export type EmisorDeNotificaciones = z.infer<typeof EmisorDeNotificacionesZod>

// ============= ALIAS DE COMPATIBILIDAD =============
// Para mantener compatibilidad con código existente durante la transición

// Alias del tipo anterior al nuevo nombre
export type SuscripcionPushDelTerminal = SuscripcionPushTerminal

// Alias de tipos renombrados
export type IdentificadorSuscrito = IdentificadorActivo
export type HistorialRecibida = NotificacionRecibida
export type HistorialEmitida = NotificacionEnviada
export type NotificacionEnElHistorial = NotificacionParaHistorial
export type TerminalPushData = SuscripcionPushTerminal
